import { motion } from 'framer-motion';
import { FaBrain, FaChartLine, FaLightbulb, FaUsers } from 'react-icons/fa';

import OptimizedImage from '../OptimizedImage';

const AppSection = () => {
  const features = [
    {
      icon: <FaLightbulb className="w-6 h-6 text-secondary" />,
      title: "personalized insights",
      description: "Receive practical guidance and tools tailored to your goals and experiences."
    },
    {
      icon: <FaBrain className="w-6 h-6 text-secondary" />,
      title: "agentic coach",
      description: "An AI-powered coach supported by research engages in dialogue to provide insights and exercises to support your needs."
    },
    {
      icon: <FaChartLine className="w-6 h-6 text-secondary" />,
      title: "progress tracking",
      description: "Monitor your growth over time with intuitive visuals and meaningful milestones metrics."
    },
    {
      icon: <FaUsers className="w-6 h-6 text-secondary" />,
      title: "community learning",
      description: "Connect with friends, family, and spouses on similar journeys and learn from shared experiences."
    }
  ];

  return (
    <section id="app" className="section bg-surface dark:bg-panel/90">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
            a relationship coach in your pocket
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            The konnect better app is an intelligent coaching companion designed to strengthen relationships. With meaningful conversations, progress tracking, and engaging activities, it can adapt to your needs and give you tools to stay connected anytime, anywhere.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            {/* <h3 className="text-2xl font-bold text-primary dark:text-theme-primary mb-6">
              Your Learning Companion
            </h3>
            <p className="text-theme-secondary mb-6">
              The konnect better app is more than just another wellness tool. It's an intelligent companion that understands your unique psychological needs and helps you develop meaningful insights and habits.
            </p> */}
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <div className="mr-4 p-2 bg-brand-primary/10 dark:bg-white/10 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-primary dark:text-theme-primary mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-theme-secondary">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-8"
            >
              <div className="flex flex-col items-center">
                <span className="bg-brand-primary text-white text-[10px] font-semibold px-3 py-1 rounded-full mb-3">
                  Coming Soon
                </span>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    disabled
                    className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg border border-theme bg-card dark:bg-panel text-theme-primary dark:text-theme-primary shadow-surface opacity-70 cursor-not-allowed"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.5646 12.9838C17.5458 10.0786 20.0278 8.60303 20.1382 8.53394C18.6788 6.47992 16.4118 6.19144 15.6003 6.17323C13.7451 5.97852 11.9535 7.27303 11.0105 7.27303C10.0457 7.27303 8.60117 6.19144 7.04354 6.22786C5.01862 6.26428 3.12558 7.43766 2.06447 9.25831C-0.118528 12.9656 1.44239 18.4285 3.52558 21.2701C4.56748 22.6738 5.78006 24.2496 7.37558 24.1905C8.93321 24.1314 9.51319 23.1848 11.3684 23.1848C13.2018 23.1848 13.7451 24.1905 15.3625 24.1496C17.0344 24.1314 18.0763 22.7277 19.0847 21.3058C20.2973 19.6709 20.7861 18.0769 20.8049 17.9996C20.7673 17.9814 17.5864 16.6732 17.5646 12.9838Z" />
                      <path d="M14.6005 4.05241C15.4557 2.98903 16.0357 1.52262 15.8707 0.0380859C14.6005 0.0926955 13.0429 0.892762 12.1513 1.93793C11.3689 2.85517 10.6667 4.36069 10.8499 5.80889C12.2547 5.90619 13.7267 5.09697 14.6005 4.05241Z" />
                    </svg>
                    <div>
                      <p className="text-xs">Download on the</p>
                      <p className="text-sm font-semibold">App Store</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    disabled
                    className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg border border-theme bg-card dark:bg-panel text-theme-primary dark:text-theme-primary shadow-surface opacity-70 cursor-not-allowed"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.22183 0.313477C0.988258 0.565283 0.871582 0.908142 0.871582 1.34206V22.6572C0.871582 23.0911 0.988258 23.434 1.22183 23.6858L1.30094 23.7649L13.1048 11.9611V11.8781V11.7951L1.30094 -0.00878906L1.22183 0.313477Z" />
                      <path d="M17.2974 16.1538L13.1047 11.9611V11.8781V11.7951L17.2974 7.60254L17.3765 7.6816L22.3047 10.4358C23.6878 11.2111 23.6878 12.5451 22.3047 13.3204L17.3765 16.0746L17.2974 16.1538Z" />
                      <path d="M17.3766 16.0746L13.1048 11.8781L1.22192 23.6858C1.69305 24.1988 2.47748 24.1988 3.36748 23.7649L17.3766 16.0746Z" />
                      <path d="M17.3766 7.68165L3.36748 -0.00878906C2.47748 -0.442789 1.69305 -0.442789 1.22192 0.0702734L13.1048 11.7951L17.3766 7.68165Z" />
                    </svg>
                    <div>
                      <p className="text-xs">Get it on</p>
                      <p className="text-sm font-semibold">Google Play</p>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2 flex justify-center"
          >
            <div className="relative">
              {/* App screenshot */}
              <div className="bg-card dark:bg-panel rounded-3xl shadow-xl p-2 max-w-xs">
                <div className="rounded-2xl aspect-[9/16] overflow-hidden">
                  <OptimizedImage
                    src="/images/app.png" 
                    alt="Konnect Better App Notifications"
                    sizes="(max-width: 768px) 100vw, 384px"
                  />
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -z-10 -top-6 -left-6 w-24 h-24 bg-brand-secondary/30 rounded-full blur-xl"></div>
              <div className="absolute -z-10 -bottom-8 -right-8 w-32 h-32 bg-brand-primary/20 rounded-full blur-xl"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppSection;
